import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Images, Loader2, X } from "lucide-react";
import { errMsg } from "../../lib/api";
import { useClient } from "../../context/ClientContext";
import { compressImage } from "../../lib/image";
import { PhotoThumb } from "./PhotoThumb";

const MAX = 4;

export const PhotoPicker = ({ photos, onChange, onUploading, testid }) => {
  const { client: api } = useClient();
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [pending, setPending] = useState(0);
  const left = MAX - photos.length - pending;

  const handle = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(left, 0));
    e.target.value = "";
    if (!files.length) return;
    setPending((p) => p + files.length);
    onUploading(files.length);
    const added = [];
    for (const f of files) {
      try {
        const blob = await compressImage(f);
        const fd = new FormData();
        fd.append("file", blob, "photo.jpg");
        const { data } = await api.post("/uploads", fd);
        added.push(data.path);
      } catch (err) {
        toast.error(`Photo upload failed: ${errMsg(err)}`);
      }
    }
    setPending((p) => p - files.length);
    onUploading(-files.length);
    onChange([...photos, ...added]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="label !mb-0">Photos ({photos.length}/{MAX})</span>
      </div>
      {(photos.length > 0 || pending > 0) && (
        <div className="grid grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <div key={p} className="relative">
              <PhotoThumb path={p} testid={`${testid}-photo-${i}`} />
              <button type="button" onClick={() => onChange(photos.filter((x) => x !== p))} aria-label="Remove photo"
                className="absolute -right-1.5 -top-1.5 grid h-7 w-7 place-items-center rounded-full bg-foreground text-background shadow"
                data-testid={`${testid}-photo-${i}-remove-btn`}><X size={14} /></button>
            </div>
          ))}
          {Array.from({ length: pending }).map((_, i) => (
            <div key={`p${i}`} className="grid aspect-square place-items-center rounded-lg border border-dashed"><Loader2 className="animate-spin" size={18} /></div>
          ))}
        </div>
      )}
      {left > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn btn-outline" onClick={() => camRef.current?.click()} data-testid={`${testid}-take-photo-btn`}>
            <Camera size={18} /> Take photo
          </button>
          <button type="button" className="btn btn-outline" onClick={() => galRef.current?.click()} data-testid={`${testid}-gallery-btn`}>
            <Images size={18} /> Gallery
          </button>
        </div>
      )}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} data-testid={`${testid}-camera-input`} />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={handle} data-testid={`${testid}-gallery-input`} />
    </div>
  );
};
